import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";

import { select, Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";

import {
  AppState,
  loadedMenusState,
  sitesStateTableConfig,
} from "src/app/store/reducers";
import {
  getSitesList,
  getSitesExcelList,
  resetSitesState,
} from "src/app/store/actions/sites/sites.actions";

import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";

import {
  DefaultPage,
  TableEvent,
  ExportEvent,
  PageEvent,
  SortEvent,
  SearchEvent,
  FilterEvent,
  ColumnSorterInfo,
} from "src/app/models/custom-table/custom-table.model";

import { setColumnInfoCategoriesProperties } from "src/app/shared/utils/tableUtils";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";

import { BaseSitesList } from "src/app/shared/base-classes/base-sites-list/base-sites-list";
import { TranslateService } from "@ngx-translate/core";

const USAGE_CODE = "SITE";

@Component({
  selector: "app-sites-list",
  templateUrl: "./sites-list.component.html",
  styleUrls: ["./sites-list.component.scss"],
})
export class SitesListComponent extends BaseSitesList implements OnInit, OnDestroy {
  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    store: Store<AppState>,
    translate: TranslateService
  ) {
    super(store, translate);
  }

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));
    this.columnInfo$ = this.store.pipe(select(sitesStateTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return setColumnInfoCategoriesProperties(columnInfo, this.tableConfig);
        }
      })
    );

    this.loadScreenTitle();
    this.getSitesCategoriesProperties(USAGE_CODE, this.getSitesList.bind(this));
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          })
        });
      }
    });
  }

  getSitesList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getSitesList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getSitesExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getSitesExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getSitesList.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getSitesExcelList.bind(this));
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(tableColumnInfo, USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.siteListSub) {
      this.siteListSub.unsubscribe();
    }
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
    this.store.dispatch(resetSitesState());
  }
}
